import { connect } from 'react-redux';
import { Dispatch } from 'redux';

import { State } from '../../typings/state';
import { TDispatchProps, TStateProps } from '../../typings/utils';
import {
    GALLERY_DEFAULT_CATEGORIES,
    GALLERY_DEFAULT_FETCH_FILTER_NAME,
    GALLERY_DEFAULT_FETCH_ORDER,
    Order,
    ResourceType
} from '../common/typings';
import {
    changeRecentNameFilter,
    changeRecentOrder,
    changeSelectedGalleryCategories,
    fetchGalleryCategoriesData,
    GalleryAction,
    hoverItem,
    previewImageItem
} from '../GalleryPane/actions';
import { getCommonGalleryDispatchToProps } from '../GalleryPane/helpers';
import {
    selectCurrentGallery,
    selectEmptyGalleryPanesMap,
    selectRecentGalleryPane,
    selectStockGalleryPane
} from '../GalleryPane/selectors';
import { GalleryPaneTypes, PreviewImageItemParams } from '../GalleryPane/typings';

import { CanvasImageGallery, CanvasImageGalleryProps } from './CanvasImageGallery';

export type StateProps = TStateProps<CanvasImageGalleryProps>;
export type DispatchProps = TDispatchProps<
    CanvasImageGalleryProps & {
        dispatchSelectedGalleryCategories: (selectedGalleryCategoriesIdsList: string[]) => void;
    },
    {
        selectGalleryCategory: (selectedCategoryId: string) => void;
    }
>;

const mapDispatchToProps = (dispatch: Dispatch<GalleryAction>): DispatchProps => {
    const commonDispatchProps = getCommonGalleryDispatchToProps(dispatch);

    return {
        ...commonDispatchProps,
        previewImageItem: (paneType: GalleryPaneTypes, params?: PreviewImageItemParams): void => {
            dispatch(previewImageItem(paneType, params));
        },
        hoverItem: (itemIndex?: number) => {
            dispatch(hoverItem(itemIndex));
        },
        changeRecentNameFilter: (filter: string) => {
            dispatch(changeRecentNameFilter(filter));
        },
        fetchGalleryCategoriesData: () => {
            dispatch(fetchGalleryCategoriesData());
        },
        changeRecentOrder: (order: Order) => {
            dispatch(changeRecentOrder(order));
        },
        dispatchSelectedGalleryCategories: (selectedGalleryCategoriesIdsList: string[]) => {
            dispatch(changeSelectedGalleryCategories(selectedGalleryCategoriesIdsList));
        }
    };
};

export const mapStateToProps = (state: State): StateProps => {
    const { galleries } = state.canvasEditor.canvas;
    const { isPopupOpened, imageStockCategoriesData, resources, currentResourceType, error } = galleries;

    const { currentPaneType, enabledGalleryTypes, preview, hoveredItemIndex, isBusy } = selectCurrentGallery(galleries);
    const { data } = resources[ResourceType.image].panes[currentPaneType];

    let selectedGalleryCategories = GALLERY_DEFAULT_CATEGORIES;
    let recentOrder = GALLERY_DEFAULT_FETCH_ORDER;
    let recentNameFilter = GALLERY_DEFAULT_FETCH_FILTER_NAME;

    if (enabledGalleryTypes.includes(GalleryPaneTypes.stock)) {
        const stockFilters = selectStockGalleryPane(galleries).filters;
        selectedGalleryCategories = stockFilters.selectedGalleryCategories;
    }

    if (enabledGalleryTypes.includes(GalleryPaneTypes.recent)) {
        const recentFilters = selectRecentGalleryPane(galleries).filters;

        recentOrder = recentFilters.recentOrder;
        recentNameFilter = recentFilters.recentNameFilter;
    }

    return {
        isBusy,
        currentPaneType,
        isPopupOpened: isPopupOpened && currentResourceType === ResourceType.image,
        enabledGalleryTypes,
        error,
        emptyGalleriesMap: selectEmptyGalleryPanesMap(galleries, ResourceType.image),
        preview,
        hoveredItemIndex,
        categoriesData: imageStockCategoriesData,
        selectedGalleryCategories,
        recentNameFilter,
        recentOrder,
        currentPaneCollection: data.collection
    };
};

const mergeProps = (stateProps: StateProps, dispatchProps: DispatchProps) => {
    const { selectedGalleryCategories } = stateProps;
    const { dispatchSelectedGalleryCategories } = dispatchProps;

    return {
        ...stateProps,
        ...dispatchProps,
        selectGalleryCategory: (selectedId: string) => {
            const result = Array().concat(selectedGalleryCategories);

            const indexOfSelectedId = selectedGalleryCategories.indexOf(selectedId);

            if (indexOfSelectedId === -1) {
                result.push(selectedId);
            } else {
                result.splice(indexOfSelectedId, 1);
            }
            dispatchSelectedGalleryCategories(result);
        }
    };
};

export const CanvasImageGalleryContainer = connect<StateProps, DispatchProps, {}, CanvasImageGalleryProps>(
    mapStateToProps,
    mapDispatchToProps,
    mergeProps
)(CanvasImageGallery);
